import { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { UserPlus } from "lucide-react";
import { callApi } from "../api/api";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";
import { useBreakpoint } from "../hooks/useBreakpoint";

export default function Users() {
  const { instance, accounts } = useMsal();

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [entraQuery, setEntraQuery] = useState("");
  const [entraResults, setEntraResults] = useState([]);
  const [entraLoading, setEntraLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    isAdmin: false,
    isActive: true,
  });

  const [toast, setToast] = useState(null);
  const [duplicateEmail, setDuplicateEmail] = useState("");
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  useBreakpoint(1024); // keep hook for responsive card breakpoint; no direct local usage

  /* ================= LOAD USERS ================= */
  const loadUsers = useCallback(
    async (searchText = "", currentPage = 1) => {
      try {
        setLoading(true);

        const finalSearch =
          searchText !== undefined && searchText !== null ? searchText : "";

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/users?page=${currentPage}&pageSize=${pageSize}&search=${encodeURIComponent(finalSearch)}`,
        );

        const response = res?.data || {};

        setUsers(response.data || []);
        setTotalRecords(response.total || 0);
      } catch (err) {
        console.error(err);
        setUsers([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [instance, accounts, pageSize],
  );

  /* ================= HELPERS ================= */

  useEffect(() => {
    if (!showAdd || !entraQuery || entraQuery.length < 2) {
      setEntraResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setEntraLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/user/entra-users?search=${entraQuery}`,
        );

        setEntraResults(res?.data?.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error("Entra search failed", err);
        setEntraResults([]);
      } finally {
        setEntraLoading(false);
      }
    }, 400); // debounce

    return () => clearTimeout(timer);
  }, [entraQuery, showAdd, instance, accounts]);

  const openEditUser = useCallback((u) => {
    if (!u) return;
    setSelectedUser(u);
    setForm({
      firstName: u.first_name || "",
      lastName: u.last_name || "",
      email: u.email_address,
      isAdmin: u.is_admin,
      isActive: u.is_active,
    });
    setShowEdit(true);
  }, []);

  const formatName = useCallback((u) => {
    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
    if (name) return name;
    return u.email_address?.split("@")[0] || "Unknown";
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString();
  }, []);

  const mapUserCard = useCallback(
    (u) => ({
      id: u.user_id || u.id || u.email_address,
      icon: <span aria-hidden>👤</span>,
      tone: u.is_active ? "success" : "warning",
      title: formatName(u),
      subtitle: u.email_address || "—",
      meta: [
        { label: "Admin", value: u.is_admin ? "Yes" : "No" },
        { label: "Created", value: formatDate(u.created_at) },
      ],
      badges: [
        {
          label: "Status",
          value: u.is_active ? "Active" : "Inactive",
          tone: u.is_active ? "success" : "danger",
        },
      ],
      actions: [
        {
          label: "Edit",
          onClick: () => openEditUser(u),
          icon: "✏️",
        },
      ],
    }),
    [formatName, formatDate, openEditUser],
  );

  const mapUserDrawer = useCallback(
    (u) => ({
      title: formatName(u),
      fields: [
        { label: "Email", value: u.email_address || "—" },
        { label: "Admin", value: u.is_admin ? "Yes" : "No" },
        { label: "Status", value: u.is_active ? "Active" : "Inactive" },
        { label: "Created", value: formatDate(u.created_at) },
      ],
      primaryAction: {
        label: "Edit User",
        onClick: () => openEditUser(u),
      },
    }),
    [formatDate, formatName, openEditUser],
  );

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!showAdd) {
      setDuplicateEmail("");
      setIsCheckingDuplicate(false);
      return;
    }

    const normalizedEmail = (form.email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      setDuplicateEmail("");
      setIsCheckingDuplicate(false);
      return;
    }

    let cancelled = false;
    setIsCheckingDuplicate(true);

    (async () => {
      try {
        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/users?page=1&pageSize=1&search=${encodeURIComponent(normalizedEmail)}`,
        );

        const list = res?.data?.data || [];
        const found = list.some(
          (u) => (u.email || u.email_address || "").toLowerCase() === normalizedEmail,
        );

        if (cancelled) return;

        if (found) {
          setDuplicateEmail(form.email);
          setErrors((prev) => ({ ...prev, email: "User already exists" }));
        } else {
          setDuplicateEmail("");
          setErrors((prev) =>
            prev.email === "User already exists" ? { ...prev, email: "" } : prev,
          );
        }
      } catch (err) {
        console.error("Duplicate check failed", err);
        if (cancelled) return;
        setDuplicateEmail("");
        setErrors((prev) =>
          prev.email === "User already exists" ? { ...prev, email: "" } : prev,
        );
      } finally {
        if (!cancelled) {
          setIsCheckingDuplicate(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showAdd, form.email, instance, accounts]);

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      isAdmin: false,
      isActive: true,
    });

    setEntraQuery("");

    setEntraResults([]);

    setShowDropdown(false);

    setErrors({});
  };

  const clearEntraSelection = () => {
    setForm((prev) => ({
      ...prev,
      email: "",
      firstName: "",
      lastName: "",
    }));
    setDuplicateEmail("");
    setIsCheckingDuplicate(false);
    setErrors((prev) => (prev.email ? { ...prev, email: "" } : prev));
  };

  const handleEntraQueryChange = (value) => {
    setEntraQuery(value);
    setShowDropdown(true);

    if (!value.trim()) {
      clearEntraSelection();
      setEntraResults([]);
      setShowDropdown(false);
      return;
    }

    if (form.email && !value.includes(form.email)) {
      clearEntraSelection();
    }
  };

  const validate = () => {
    const err = {};

    if (!form.firstName?.trim()) {
      err.firstName = "First name is required";
    }

    if (!form.email?.trim()) {
      err.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      err.email = "Invalid email format";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };
  /* ================= ACTIONS ================= */
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isNarrowToolbar, setIsNarrowToolbar] = useState(false);
  const [isAddTooltipVisible, setIsAddTooltipVisible] = useState(false);
  const addTooltipTouchTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (addTooltipTouchTimer.current) {
        clearTimeout(addTooltipTouchTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowToolbar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (accounts.length) {
      loadUsers(debouncedSearch, page);
    }
  }, [accounts.length, debouncedSearch, page, loadUsers]);

  const handleAdd = async () => {
    if (!validate()) return;
    if (duplicateEmail) {
      setErrors((prev) => ({
        ...prev,
        email: prev.email || "User already exists",
      }));
      return;
    }
    if (isCheckingDuplicate) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "POST",
        "/api/admin/users",
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          isAdmin: form.isAdmin,
          isActive: form.isActive,
        },
      );

      // 🔥 CRITICAL FIX
      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Failed to create user");
      }

      showToast("User created successfully");

      setShowAdd(false);
      resetForm();
      loadUsers();
    } catch (err) {
      console.error(err);

      showToast(
        err?.response?.data?.message ||
          err.message ||
          "Access denied or failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isCreateDisabled =
    submitting || Boolean(duplicateEmail) || isCheckingDuplicate;

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "PUT",
        `/api/admin/users/${selectedUser.user_id}`,
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          isAdmin: form.isAdmin,
          isActive: form.isActive,
        },
      );

      // 🔥 HANDLE ACCESS DENIED / FAILURE
      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Update failed");
      }

      showToast("User updated successfully");

      // ✅ CLEANUP STATE
      setShowEdit(false);
      setSelectedUser(null);
      resetForm();

      // 🔄 RELOAD DATA
      await loadUsers();
    } catch (err) {
      console.error("Edit failed:", err);

      showToast(
        err?.response?.data?.message || err.message || "Update failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "DELETE",
        `/api/admin/users/${selectedUser.user_id}`,
      );

      // 🔥 HANDLE FAILURE (VERY IMPORTANT)
      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Delete failed");
      }

      showToast("User deleted successfully");

      // ✅ CLEANUP
      setShowDelete(false);
      setSelectedUser(null);

      // 🔄 REFRESH DATA
      await loadUsers();
    } catch (err) {
      console.error("Delete failed:", err);

      showToast(
        err?.response?.data?.message || err.message || "Delete failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAddUserModal = () => {
    resetForm();
    setSelectedUser(null);
    setShowAdd(true);
  };

  const showAddTooltip = () => setIsAddTooltipVisible(true);

  const hideAddTooltip = () => setIsAddTooltipVisible(false);

  const handleAddTooltipTouchStart = () => {
    if (addTooltipTouchTimer.current) {
      clearTimeout(addTooltipTouchTimer.current);
    }
    setIsAddTooltipVisible(true);
  };

  const handleAddTooltipTouchEnd = () => {
    addTooltipTouchTimer.current = setTimeout(() => {
      setIsAddTooltipVisible(false);
    }, 1200);
  };

  /* ================= UI ================= */

  const paginationProps =
    totalRecords > 0
      ? {
          page,
          totalPages: Math.max(1, Math.ceil(totalRecords / pageSize)),
          totalRecords,
          pageSize,
          currentPageCount: users.length,
          onPageChange: setPage,
        }
      : null;

  return (
    <div className="page-container" style={styles.container}>
      <div
        style={{
          ...styles.header,
          ...(isNarrowToolbar ? styles.headerNarrow : null),
        }}
      >
        <div
          style={isNarrowToolbar ? styles.headerTitleBlock : undefined}
        >
          <h2 style={styles.title}>Users</h2>
          <p style={styles.sub}>
            Browse all portal users and their assigned access.
          </p>
        </div>

        {isNarrowToolbar ? (
          <div style={styles.addBtnWrap}>
            <button
              type="button"
              style={{
                ...styles.addBtn,
                ...styles.addBtnCompact,
              }}
              aria-label="Add user"
              aria-describedby={
                isAddTooltipVisible ? "users-add-tooltip" : undefined
              }
              onMouseEnter={showAddTooltip}
              onMouseLeave={hideAddTooltip}
              onFocus={showAddTooltip}
              onBlur={hideAddTooltip}
              onTouchStart={handleAddTooltipTouchStart}
              onTouchEnd={handleAddTooltipTouchEnd}
              onClick={openAddUserModal}
            >
              <UserPlus size={20} strokeWidth={2} aria-hidden />
            </button>
            {isAddTooltipVisible && (
              <div
                id="users-add-tooltip"
                style={styles.addTooltip}
                role="tooltip"
              >
                <span style={styles.addTooltipArrow} aria-hidden />
                Add user
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            style={styles.addBtn}
            onClick={openAddUserModal}
          >
            + Add user
          </button>
        )}
      </div>

      <div
        style={{
          ...styles.toolbar,
          ...(isNarrowToolbar ? styles.toolbarNarrow : null),
        }}
      >
        <input
          placeholder="Search by email or user name"
          style={{
            ...styles.search,
            ...(isNarrowToolbar ? styles.searchNarrow : null),
          }}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e?.target?.value || "");
          }}
        />
      </div>

      <ResponsiveTableCards
        breakpoint={1024}
        data={users}
        loading={loading}
        emptyTitle="No records found"
        emptySub="No users match your filters."
        listTitle="Users"
        listSubtitle="Browse portal users and their access"
        cardMapper={mapUserCard}
        drawerMapper={mapUserDrawer}
        pagination={paginationProps || undefined}
        renderTable={() => (
          <>
            <div style={styles.tableCard}>
              {loading ? (
                <div style={styles.empty}>Loading...</div>
              ) : (
                <ScrollableTable>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>FULL NAME</th>
                    <th style={styles.th}>EMAIL</th>
                    <th style={styles.th}>ADMIN</th>
                    <th style={styles.th}>STATUS</th>
                    <th style={styles.th}>CREATED</th>
                    <th style={styles.thRight}>ACTIONS</th>
                  </tr>
                </thead>

                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          padding: "32px",
                          textAlign: "center",
                          color: "#64748b",
                          fontSize: "14px",
                        }}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr
                        key={u.user_id}
                        style={styles.row}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#fff")
                        }
                      >
                        <td style={styles.td}>
                          {u.first_name + " " + u.last_name ||
                            u.email_address.split("@")[0]}
                        </td>

                        <td style={{ ...styles.td, color: "#2563eb" }}>
                          {u.email_address}
                        </td>

                        <td style={styles.td}>
                          {u.is_admin ? (
                            <span style={styles.adminBadge}>ADMIN</span>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={styles.td}>
                          {u.is_active ? (
                            <span style={styles.activeBadge}>● Active</span>
                          ) : (
                            <span style={styles.inactiveBadge}>● Inactive</span>
                          )}
                        </td>

                        <td style={styles.td}>
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>

                        <td style={styles.tdRight}>
                          <span
                            style={styles.icon}
                            onClick={() => {
                              setSelectedUser(u);
                              setForm({
                                firstName: u.first_name || "",
                                lastName: u.last_name || "",
                                email: u.email_address,
                                isAdmin: u.is_admin,
                                isActive: u.is_active,
                              });
                              setShowEdit(true);
                            }}
                          >
                            ✏️
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </ScrollableTable>
              )}
            </div>

            {paginationProps && <Pagination {...paginationProps} />}
          </>
        )}
      />

      {/* ADD / EDIT */}
      {(showAdd || showEdit) && (
        <Modal
          title={showAdd ? "Add user" : "Edit user"}
          onClose={() => {
            resetForm();
            setSelectedUser(null);
            setShowAdd(false);
            setShowEdit(false);
          }}
        >
          <Input
            label="First name"
            value={form.firstName}
            onChange={(v) => {
              setForm({ ...form, firstName: v });
              setErrors((prev) => ({ ...prev, firstName: "" }));
            }}
          />

          {errors.firstName && (
            <div style={styles.errorText}>{errors.firstName}</div>
          )}
          <Input
            label="Last name"
            value={form.lastName}
            onChange={(v) => setForm({ ...form, lastName: v })}
          />
          {/* ONLY SHOW IN ADD MODE */}
          {showAdd && (
            <>
              <label style={styles.label}>Search User (Entra ID)</label>

              <input
                style={styles.input}
                placeholder="Type name or email..."
                value={entraQuery}
                onChange={(e) => handleEntraQueryChange(e.target.value)}
                onFocus={(e) => (e.target.style.border = "1px solid #2563eb")}
                onBlur={(e) => (e.target.style.border = "1px solid #dbe2ea")}
                disabled={showEdit}
              />

              {/* DROPDOWN */}
              {showDropdown && entraResults.length > 0 && (
                <div style={styles.dropdownBox} className="scrollable">
                  {entraResults.map((u) => (
                    <div
                      key={u.id}
                      style={styles.dropdownItem}
                      onClick={() => {
                        // ✅ fill form
                        setForm({
                          ...form,
                          email: u.email,
                          firstName: u.name?.split(" ")[0] || "",
                          lastName: u.name?.split(" ").slice(1).join(" ") || "",
                        });

                        setEntraQuery(`${u.name} (${u.email})`);
                        setShowDropdown(false);
                        setEntraResults([]);
                      }}
                    >
                      <div style={{ fontWeight: "500" }}>{u.name}</div>
                      <div style={{ fontSize: styles.helper.fontSize, color: "#64748b" }}>
                        {u.email}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {entraLoading && <div style={styles.helper}>Searching...</div>}
            </>
          )}
          {/* SELECTED EMAIL (READ ONLY) */}
          {form.email && (
            <>
              <label style={styles.label}>Email</label>
              <input
                style={{ ...styles.input, background: "#f1f5f9" }}
                value={form.email}
                disabled
              />
            </>
          )}

          {errors.email && <div style={styles.errorText}>{errors.email}</div>}

          <Toggle
            label="Admin"
            value={form.isAdmin}
            onChange={(v) => setForm({ ...form, isAdmin: v })}
          />
          <Toggle
            label="Active"
            value={form.isActive}
            onChange={(v) => setForm({ ...form, isActive: v })}
          />

          <div style={styles.footer}>
            <button
              style={styles.cancelBtn}
              onClick={() => {
                setShowAdd(false);
                setShowEdit(false);
              }}
            >
              Cancel
            </button>

            <button
              style={{
                ...styles.primaryBtn,
                ...(isCreateDisabled ? styles.primaryBtnDisabled : null),
              }}
              onClick={showAdd ? handleAdd : handleEdit}
              disabled={isCreateDisabled}
            >
              {submitting
                ? showAdd
                  ? "Creating..."
                  : "Updating..."
                : showAdd
                  ? "Create user"
                  : "Update user"}
            </button>
          </div>
        </Modal>
      )}

      {/* DELETE MODAL */}
      {showDelete && (
        <Modal title="Delete user" onClose={() => setShowDelete(false)}>
          <p>Are you sure you want to delete this user?</p>
          <button style={styles.dangerBtn} onClick={handleDelete}>
            Delete
          </button>
        </Modal>
      )}

      {/* TOAST */}
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#ef4444",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Input = ({ label, value, onChange, disabled }) => (
  <>
    <label style={styles.label}>{label}</label>
    <input
      style={{
        ...styles.input,
        background: disabled ? "#f1f5f9" : "#fff",
        cursor: disabled ? "not-allowed" : "text",
      }}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </>
);

const Toggle = ({ label, value, onChange }) => (
  <div style={styles.toggle}>
    <div
      onClick={() => onChange(!value)}
      style={{
        ...styles.switch,
        background: value ? "#0f172a" : "#e2e8f0",
      }}
    >
      <div
        style={{
          ...styles.knob,
          transform: value ? "translateX(18px)" : "translateX(2px)",
        }}
      />
    </div>
    <span style={{ fontSize: styles.input.fontSize }}>{label}</span>
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={styles.modalHeader}>
        <h3 style={{ margin: 0, fontSize: styles.title.fontSize }}>{title}</h3>
        <span style={styles.close} onClick={onClose}>
          ✕
        </span>
      </div>
      {children}
    </div>
  </div>
);

const styles = {
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    borderTop: "1px solid #e2e8f0",
  },

  pageBtn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "500",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
  },
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  /* ================= HEADER ================= */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    gap: "20px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "8px",
    fontSize: "14px",
  },

  addBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    flexShrink: 0,
  },

  headerNarrow: {
    flexWrap: "nowrap",
    alignItems: "flex-start",
  },

  headerTitleBlock: {
    flex: "1 1 0",
    minWidth: 0,
  },

  addBtnCompact: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    minWidth: "40px",
    minHeight: "40px",
  },

  addBtnWrap: {
    position: "relative",
    flexShrink: 0,
  },

  addTooltip: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "#0f172a",
    color: "#f8fafc",
    border: "1px solid #1e293b",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
    zIndex: 20,
    pointerEvents: "none",
  },

  addTooltipArrow: {
    position: "absolute",
    top: "-6px",
    right: "14px",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #0f172a",
  },

  /* ================= TOOLBAR ================= */

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "nowrap",
    background: "#fff",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  search: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  count: {
    fontSize: "13px",
    color: "#64748b",
    marginLeft: "auto",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  toolbarNarrow: {
    flexWrap: "wrap",
  },

  searchNarrow: {
    flex: "1 1 100%",
    width: "100%",
  },

  countNarrow: {
    flex: "1 1 100%",
    width: "100%",
    marginLeft: 0,
    textAlign: "center",
  },

  /* ================= TABLE ================= */

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  thead: {
    background: "#f8fafc",
  },

  th: {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    padding: "14px 16px",
    textAlign: "right",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s ease",
  },

  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#0f172a",
    verticalAlign: "middle",
  },

  tdRight: {
    padding: "16px",
    textAlign: "right",
    verticalAlign: "middle",
  },

  /* ================= BADGES ================= */

  adminBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.03em",
  },

  activeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#15803d",
    fontWeight: "500",
    fontSize: "13px",
  },

  inactiveBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#dc2626",
    fontWeight: "500",
    fontSize: "13px",
  },

  /* ================= ACTIONS ================= */

  icon: {
    cursor: "pointer",
    marginLeft: "12px",
    fontSize: "16px",
    transition: "transform 0.15s ease",
  },

  /* ================= MODAL ================= */

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
    padding: "20px",
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "500px",
    padding: "24px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
    animation: "fadeIn 0.2s ease",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  close: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#64748b",
  },

  /* ================= FORM ================= */

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "6px",
    marginTop: "14px",
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  helper: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "6px",
  },

  errorText: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "6px",
  },

  /* ================= TOGGLE ================= */

  toggle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "18px",
  },

  switch: {
    width: "42px",
    height: "22px",
    borderRadius: "999px",
    position: "relative",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  knob: {
    position: "absolute",
    top: "2px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#fff",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
  },

  /* ================= FOOTER ================= */

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "28px",
  },

  cancelBtn: {
    border: "1px solid #dbe2ea",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "500",
  },

  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
  },

  dangerBtn: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "20px",
  },

  /* ================= DROPDOWN ================= */

  dropdownBox: {
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    marginTop: "6px",
    maxHeight: "220px",
    overflowY: "auto",
    background: "#fff",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },

  dropdownItem: {
    padding: "12px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.15s ease",
  },

  /* ================= TOAST ================= */

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontWeight: "500",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 1000,
  },

  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
  },
};
